import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../../services/api';

/**
 * Runtime secrets for each environment.
 *
 * Two things shape this screen more than anything else.
 *
 * First, a value is only ever fetched for one key at a time, on an explicit
 * click. The listing carries metadata — set, placeholder, empty, length, a
 * truncated hash — and no values at all, so the page can be open on a shared
 * screen without anything sensitive on it. A revealed value hides itself again
 * after thirty seconds for the same reason.
 *
 * Second, `placeholder` is its own status rather than being folded into "set".
 * REPLACE_ME is what Terraform seeds every key with, and it is published in the
 * repository: an environment carrying it is misconfigured in a way that looks
 * configured. That distinction is the main thing this screen exists to show.
 */

type KeyStatus = 'set' | 'placeholder' | 'empty';

interface SecretKey {
  key: string;
  status: KeyStatus;
  length: number;
  fingerprint: string | null;
}

interface SecretEnvironment {
  environment: string;
  secretId: string;
  lastChanged: string | null;
  keys: SecretKey[];
  error?: string;
}

/** Values live only in component state, keyed by `${environment}:${key}`. */
type RevealedMap = Record<string, string>;

const REVEAL_TIMEOUT_MS = 30_000;

const STATUS_STYLES: Record<KeyStatus, { label: string; className: string }> = {
  set: { label: 'Set', className: 'bg-positive-bg text-positive' },
  placeholder: { label: 'Placeholder', className: 'bg-negative-bg text-negative' },
  empty: { label: 'Not set', className: 'bg-caution-bg text-caution' },
};

const isProduction = (name: string) => /^prod/i.test(name);

const SecretsComponent: React.FC = () => {
  const [environments, setEnvironments] = useState<SecretEnvironment[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [revealed, setRevealed] = useState<RevealedMap>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);

  // Cleared on unmount so a reveal timer cannot fire against a dead component.
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => () => {
    Object.values(timers.current).forEach(clearTimeout);
  }, []);

  const fetchSecrets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiService.getSecrets();
      const list: SecretEnvironment[] = res?.data?.environments || [];
      setEnvironments(list);
      setActive((current) => current || list[0]?.environment || null);
      if (!list.length) {
        setError(res?.data?.message || 'No environments are configured.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load secrets');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchSecrets(); }, [fetchSecrets]);

  const current = environments.find((env) => env.environment === active) || null;

  const hide = (mapKey: string) => {
    setRevealed((prev) => {
      const next = { ...prev };
      delete next[mapKey];
      return next;
    });
    clearTimeout(timers.current[mapKey]);
    delete timers.current[mapKey];
  };

  const reveal = async (key: string) => {
    if (!current) return;
    const mapKey = `${current.environment}:${key}`;

    if (revealed[mapKey] !== undefined) { hide(mapKey); return; }

    setBusyKey(key);
    try {
      const res = await apiService.revealSecret(current.environment, key);
      setRevealed((prev) => ({ ...prev, [mapKey]: res?.data?.value ?? '' }));
      // Auto-hide: this page is often open while screen sharing.
      timers.current[mapKey] = setTimeout(() => hide(mapKey), REVEAL_TIMEOUT_MS);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reveal value');
    } finally {
      setBusyKey(null);
    }
  };

  const confirmForProduction = (action: string, key: string) => {
    if (!current || !isProduction(current.environment)) return true;
    return window.confirm(
      `${action} ${key} in ${current.environment}.\n\n` +
      'This is production. The change takes effect the next time the service ' +
      'deploys, and the previous value is not recoverable from this screen.\n\n' +
      'Continue?',
    );
  };

  const save = async (key: string, payload: { value?: string; generate?: boolean }) => {
    if (!current) return;
    if (!confirmForProduction(payload.generate ? 'Generate a new' : 'Overwrite', key)) return;

    setBusyKey(key);
    setError(null);
    try {
      const res = await apiService.setSecret(current.environment, key, payload);
      setNotice(res?.message || `${key} saved.`);
      setEditing(null);
      hide(`${current.environment}:${key}`);
      await fetchSecrets();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusyKey(null);
    }
  };

  const remove = async (key: string) => {
    if (!current) return;
    if (!window.confirm(`Remove ${key} from ${current.environment} entirely?`)) return;
    if (!confirmForProduction('Delete', key)) return;

    setBusyKey(key);
    try {
      const res = await apiService.deleteSecret(current.environment, key);
      setNotice(res?.message || `${key} removed.`);
      await fetchSecrets();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove');
    } finally {
      setBusyKey(null);
    }
  };

  const counts = current
    ? current.keys.reduce(
      (acc, k) => ({ ...acc, [k.status]: (acc[k.status] || 0) + 1 }),
      {} as Record<string, number>,
    )
    : {};

  if (isLoading && !environments.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-ink-2 text-lg">Loading secrets…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-info-bg border border-border-default rounded-xl p-4">
        <p className="text-ink-2 text-sm">
          Values are stored in AWS Secrets Manager and read by the service{' '}
          <strong className="text-ink">when a task starts</strong>. A change here reaches the
          running service on its next deployment, not immediately. Every reveal and every write
          is recorded with your username.
        </p>
      </div>

      {error && (
        <div className="bg-negative-bg border border-negative/30 text-negative rounded-xl p-4" role="alert">
          {error}
        </div>
      )}

      {notice && (
        <div className="bg-positive-bg border border-positive/30 text-positive rounded-xl p-4 flex justify-between items-start gap-4" role="status">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-positive/70 hover:text-positive" aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Environments">
        {environments.map((env) => (
          <button
            key={env.environment}
            role="tab"
            aria-selected={env.environment === active}
            onClick={() => setActive(env.environment)}
            className={`px-4 py-2 rounded-xl border text-sm font-semibold transition-colors ${
              env.environment === active
                ? 'bg-surface-active border-border-strong text-ink'
                : 'bg-surface border-border-subtle text-ink-2 hover:bg-surface-hover'
            }`}
          >
            {env.environment}
            {isProduction(env.environment) && (
              <span className="ml-2 text-xs font-bold text-negative">LIVE</span>
            )}
          </button>
        ))}
      </div>

      {current?.error && (
        <div className="bg-caution-bg border border-caution/30 text-caution rounded-xl p-4">
          Could not read <code>{current.secretId}</code>: {current.error}
        </div>
      )}

      {current && !current.error && (
        <>
          {isProduction(current.environment) && (
            <div className="bg-negative-bg border-2 border-negative/40 rounded-xl p-4">
              <p className="text-negative font-semibold text-sm">
                This is production. Changes here affect live payments, email and shipping.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(['set', 'placeholder', 'empty'] as KeyStatus[]).map((status) => (
              <div key={status} className="bg-surface rounded-2xl p-4 border border-border-subtle shadow-sm">
                <h3 className="text-sm font-semibold text-ink-2 mb-1">{STATUS_STYLES[status].label}</h3>
                <div className="text-3xl font-bold text-ink">{counts[status] || 0}</div>
              </div>
            ))}
            <div className="bg-surface rounded-2xl p-4 border border-border-subtle shadow-sm">
              <h3 className="text-sm font-semibold text-ink-2 mb-1">Last changed</h3>
              <div className="text-sm font-semibold text-ink">
                {current.lastChanged ? new Date(current.lastChanged).toLocaleString() : 'Unknown'}
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-2xl border border-border-subtle shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-sunken">
                  <tr className="text-left text-ink-2">
                    <th scope="col" className="px-4 py-3 font-semibold">Key</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Status</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Length</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Fingerprint</th>
                    <th scope="col" className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {current.keys.map((entry) => {
                    const mapKey = `${current.environment}:${entry.key}`;
                    const shown = revealed[mapKey];
                    const isEditing = editing?.key === entry.key;
                    const busy = busyKey === entry.key;

                    return (
                      <tr key={entry.key} className="border-t border-border-subtle align-top">
                        <td className="px-4 py-3">
                          <code className="text-ink font-semibold">{entry.key}</code>
                          {shown !== undefined && (
                            <div className="mt-2 p-2 bg-surface-sunken rounded-lg border border-border-default">
                              <code className="text-ink break-all text-xs">{shown || '(empty)'}</code>
                              <div className="mt-1 text-xs text-ink-3">Hides automatically after 30 seconds.</div>
                            </div>
                          )}
                          {isEditing && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              <input
                                type="password"
                                autoComplete="new-password"
                                value={editing.value}
                                onChange={(e) => setEditing({ key: entry.key, value: e.target.value })}
                                placeholder={`New value for ${entry.key}`}
                                aria-label={`New value for ${entry.key}`}
                                className="flex-1 min-w-[16rem] px-3 py-2 rounded-lg border border-border-default text-ink"
                              />
                              <button
                                onClick={() => save(entry.key, { value: editing.value })}
                                disabled={busy || editing.value.length === 0}
                                className="px-3 py-2 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditing(null)}
                                className="px-3 py-2 rounded-lg border border-border-default text-ink-2 text-xs font-semibold"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[entry.status].className}`}>
                            {STATUS_STYLES[entry.status].label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-ink-2">{entry.length || '—'}</td>
                        <td className="px-4 py-3">
                          <code className="text-ink-3 text-xs">{entry.fingerprint || '—'}</code>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2 justify-end">
                            <button
                              onClick={() => reveal(entry.key)}
                              disabled={busy}
                              className="px-3 py-1.5 rounded-lg border border-border-default text-ink-2 text-xs font-semibold hover:bg-surface-hover disabled:opacity-40"
                            >
                              {shown !== undefined ? 'Hide' : 'Reveal'}
                            </button>
                            <button
                              onClick={() => setEditing(isEditing ? null : { key: entry.key, value: '' })}
                              disabled={busy}
                              className="px-3 py-1.5 rounded-lg border border-border-default text-ink-2 text-xs font-semibold hover:bg-surface-hover disabled:opacity-40"
                            >
                              {isEditing ? 'Close' : 'Set'}
                            </button>
                            <button
                              onClick={() => save(entry.key, { generate: true })}
                              disabled={busy}
                              title="Replace with 64 characters of server-generated randomness"
                              className="px-3 py-1.5 rounded-lg border border-border-default text-ink-2 text-xs font-semibold hover:bg-surface-hover disabled:opacity-40"
                            >
                              Generate
                            </button>
                            <button
                              onClick={() => remove(entry.key)}
                              disabled={busy}
                              className="px-3 py-1.5 rounded-lg border border-negative/30 text-negative text-xs font-semibold hover:bg-negative-bg disabled:opacity-40"
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-ink-3 text-xs">
            Generate is for secrets this system issues itself, such as a superadmin password or a
            shared scheduler secret. Keys issued by Stripe, SendGrid, Shippo, Google or Meta have
            to be pasted — a generated value would simply be wrong.
          </p>
        </>
      )}
    </div>
  );
};

export default SecretsComponent;
